import React, { useState } from 'react';
import styled from 'styled-components';
import TelnyxLogo from '@svg/telnyx';

import { Command, State, tabActions } from '@store/index';
import { Card } from '@common/Card';
import { Divider, Stack, TextField } from '@mui/material';
import { Label } from '@common/Label';
import { useDispatch, useSelector } from 'react-redux';

import { useTheme } from '../../../../styles';
import { Option } from '@common/BucketInput/BucketField';
import {
  TTBackupOutput,
  TTBackupToolChoice,
  TTMaxResults,
  TTMaxTokens,
  TTSearchTerm,
  TTSearchType,
} from './BucketSearch';
import { NodeInputExpression } from '../NodeInput';
import { NodeField } from '@components/Flow/Nodes';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const TTQueryParameters =
  'Set to true if you want to show the LLM the query parameters of each endpoint matched.';
export const TTBodyParameters = 'Set to true if you want to show the LLM the body parameters of each endpoint matched.';
export const TTResponse = 'Set to true if you want to show the LLM all responses a matched endpoint can receive.';
export const TTWebhooks = 'Set to true if you want to show the LLM all webhooks a matched endpoint can receive.';
export const TTSDKSamples =
  'Set to a specific SDK name in lowercase for a specific SDK sample. For example, "python" will return Python SDK samples. If left blank, set to false or an invalid SDK name is used, all SDK samples will be returned.';

export const APISearch: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const commands = useSelector((state: State) => state.tabs.active.commands);
  const theme = useTheme();

  const state = command.state[field.id];
  const enabled = state.enabled;

  const searchType = state.bucket_search_type;
  const maxResults = state.max_results as number;
  const maxTokens = state.max_tokens as number;
  const searchDescriptor = state.search_descriptor;
  const queryParameters = state.query_parameters;
  const bodyParameters = state.body_parameters;
  const responses = state.responses;
  const webhooks = state.webhooks;
  const backupOutput = state.backup_output;
  const backupToolChoice = state.backup_tool_choice;
  const sdk = state.sdk;

  const [collapsed, setCollapsed] = useState(!enabled);

  const tool_names: any[] = commands
    .filter((command) => command.config.identifier === 'Tool')
    .map((tool) => tool?.state?.name || null)
    // .filter((name) => name && name !== command?.state?.name)
    .map((name) => ({ label: name, value: name }));

  const searchTypeOptions: Option[] = [
    { label: 'List & Search', value: 'list_and_search' },
    { label: 'Direct Search', value: 'direct_search' },
    { label: 'List', value: 'list' },
  ];

  const toggleEnabled = (newVal: boolean) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'enabled',
        value: newVal,
      })
    );

    if (newVal) setCollapsed(false);
  };

  const body = (
    <Container>
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'Search Term',
          tooltip: TTSearchTerm,
          placeholder: 'Term',
          value: searchDescriptor,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'search_descriptor',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        options={searchTypeOptions}
        override={{
          title: 'Search Method',
          tooltip: TTSearchType,
          placeholder: 'Select an index search type',
          value: searchType,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'bucket_search_type',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <Stack display='grid' gridTemplateColumns='1fr 1fr' gap='0.5rem'>
        <Stack spacing='8px'>
          <Label title='Max Tokens' tooltip={TTMaxTokens} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Tokens'
            value={maxTokens}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 0) return;

              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_tokens',
                  value: Number.parseInt(e.target.value),
                })
              );
            }}
          />
        </Stack>
        <Stack spacing='8px'>
          <Label title='Max Results' tooltip={TTMaxResults} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Results'
            value={maxResults}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 1) return;

              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'max_results',
                  value: Number.parseInt(e.target.value),
                })
              );
            }}
          />
        </Stack>
      </Stack>
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'Query Parameters',
          tooltip: TTQueryParameters,
          value: queryParameters,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'query_parameters',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'Body Parameters',
          tooltip: TTBodyParameters,
          value: bodyParameters,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'body_parameters',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'Responses',
          tooltip: TTResponse,
          value: responses,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'responses',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        override={{
          title: 'Webhooks',
          tooltip: TTWebhooks,
          value: webhooks,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'webhooks',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      <NodeInputExpression
        field={field}
        command={command}
        allowToolArgs
        options={[
          { label: 'None', value: 'none' },
          { label: 'All', value: 'all' },
          { label: 'python', value: 'python' },
          { label: 'javascript', value: 'javascript' },
          { label: '.net', value: '.net' },
          { label: 'ruby', value: 'ruby' },
          { label: 'php', value: 'php' },
          { label: 'java', value: 'java' },
        ]}
        override={{
          title: 'SDK Code Sample',
          tooltip: TTSDKSamples,
          value: sdk,
          optionType: true,
          update: (fieldName, value) => {
            dispatch(
              tabActions.nodeInputNestedUpdate({
                nodeId: command.node.id,
                fieldId: field.id,
                nestedFieldName: 'sdk',
                fieldName,
                value,
              })
            );
          },
        }}
      />
      {backupOutput || backupToolChoice ? <Divider /> : null}
      {backupOutput && (
        <NodeInputExpression
          field={field}
          command={command}
          allowToolArgs
          override={{
            title: 'Backup Output',
            tooltip: TTBackupOutput,
            placeholder: 'There was no documentation found for the users question.',
            value: backupOutput,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'backup_output',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
      {backupToolChoice && (
        <NodeInputExpression
          field={field}
          command={command}
          options={[{ label: 'none', value: 'none' }, { label: 'auto', value: 'auto' }, ...tool_names]}
          override={{
            title: 'Backup Tool Choice',
            tooltip: TTBackupToolChoice,
            value: backupToolChoice,
            optionType: true,
            update: (fieldName, value) => {
              dispatch(
                tabActions.nodeInputNestedUpdate({
                  nodeId: command.node.id,
                  fieldId: field.id,
                  nestedFieldName: 'backup_tool_choice',
                  fieldName,
                  value,
                })
              );
            },
          }}
        />
      )}
    </Container>
  );

  return (
    <Card
      title='Telnyx API Search'
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      enabled={enabled}
      setEnabled={toggleEnabled}
      icon={<TelnyxLogo height={17} color={theme.telnyx.blue} />}
      description={field.description}
      descriptionBorder
      body={body}
    />
  );
};
